import React from 'react';
import AdminList, {
	ArticleCountCellRenderer,
	NameCellRenderer
} from '../../ui/admin/AdminList';
import { useQuery } from '@apollo/react-hooks';
import { CategoryItemNode, CategoriesTreeRowItem } from '../../../shared/types';
import { createStyles, makeStyles } from '@material-ui/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { CATEGORIES_LIST_TREE } from '../../../gql/categories';
import { useArticleFilter } from '../../providers/admin/ArticleFilterProvider';
import { agGridRowDataFormatter } from '../../../utils/ag-grid.utils';
import { premiumChannelsEnabled } from '../../../utils/utils';
import { formatPlural } from '../../../utils/string.utils';
import { Loader } from 'neon-design-system';
import { TextOverlapping } from '../../ui/TextOverlapping';

const name = premiumChannelsEnabled() ? 'Topics' : 'Categories';
const apiName = premiumChannelsEnabled() ? 'topics' : 'categories';

const useStyles = makeStyles(() => createStyles({
	gridHeader: {
		fontWeight: 'bold'
	}
}));

const CategoryList = (): JSX.Element => {

	const classes = useStyles();
	const articleFilter = useArticleFilter();

	const columnDefs: ColDef[] = [
		{
			colId: 'category-description-col',
			headerName: 'Description',
			headerClass: classes.gridHeader,
			field: 'description',
			sortable: false,
			width: 464,
			cellRendererFramework: (props: ICellRendererParams) => <TextOverlapping value={props.value}	maxLength={200} maxWidth={360} />,
			menuTabs: []
		},
		{
			colId: 'category-articles-col',
			headerName: 'Articles',
			headerClass: classes.gridHeader,
			field: 'articleCount',
			cellRendererFramework: (props: ICellRendererParams) => <ArticleCountCellRenderer value={props.value} filter={articleFilter} context={props.context} filterData={{_id: props.data._id, name: props.data.name, type: 'topic'}} />,
			sortable: false,
			width: 160,
			menuTabs: []
		},
		{
			colId: 'category-subscribers-col',
			headerName: 'Followers',
			headerClass: classes.gridHeader,
			field: 'subscribersCount',
			valueFormatter: (params) => params.value ? formatPlural(params.value, 'user') : 'No users',
			sortable: false,
			width: 160,
			menuTabs: []
		},
		{
			headerName: 'Views',
			field: 'views',
			width: 160,
			sortable: false,
			valueFormatter: (params) => params.value ? formatPlural(params.value, 'view') : 'No views',
			menuTabs: []
		}
	];

	const autoGroupColumnDef: ColDef = {
		menuTabs: [],
		sortable: false,
		headerName: 'Name',
		minWidth: 380,
		cellRendererParams: {
			suppressCount: true,
			innerRendererFramework: (props: any) => (
				<NameCellRenderer {...props} apiName={apiName} />
			)
		}

	};

	const queryCategoryTree = useQuery(CATEGORIES_LIST_TREE, { fetchPolicy: 'network-only' });

	if (queryCategoryTree.loading) return <Loader />;

	const treeData = (queryCategoryTree?.data?.categoriesAsTree as CategoryItemNode[]) ?? [];
	const treeRowData: CategoriesTreeRowItem[] = [];

	agGridRowDataFormatter(treeData, treeRowData);

	return (
		<AdminList
			name={name}
			items={treeRowData}
			loading={queryCategoryTree.loading}
			error={queryCategoryTree.error}
			columns={columnDefs}
			autoGroupColumn={autoGroupColumnDef}
			showAdd={true}
			treeData={true}
		/>
	);
};

export default CategoryList;
